import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { SiteIcon } from "./SiteIcon";

export interface LightboxItem {
  src: string;
  cap: string;
  /** Optional link shown in the caption (e.g. "View product"). */
  href?: string;
}

interface LightboxProps {
  items: readonly LightboxItem[];
  /** Index of the open photo, or null when closed. */
  index: number | null;
  onClose: () => void;
  onIndexChange: (index: number) => void;
}

/**
 * Full-screen photo viewer (BR-WEB-022): Esc closes, ← / → move between photos, focus moves to
 * the close button on open and returns to the opener on close, and the page behind does not scroll.
 * Portalled to document.body inside a .gw-layer (which carries the site tokens) so it is always viewport-anchored:
 * an ancestor with a transform, filter, will-change or layout/paint containment becomes the containing block for
 * position:fixed descendants, and the viewer would then be page-sized and centred on the page, not the viewport.
 */
export function Lightbox({ items, index, onClose, onIndexChange }: LightboxProps) {
  const open = index !== null && items.length > 0;
  const closeRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const opener = useRef<Element | null>(null);

  // Open/close side effects: remember the opener, lock body scroll, move focus; undo on close.
  useEffect(() => {
    if (!open) return;
    opener.current = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      const el = opener.current;
      if (el instanceof HTMLElement) el.focus();
    };
  }, [open]);

  useEffect(() => {
    if (!open || index === null) return;
    const count = items.length;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      } else if (e.key === "ArrowLeft") {
        onIndexChange((index - 1 + count) % count);
      } else if (e.key === "ArrowRight") {
        onIndexChange((index + 1) % count);
      } else if (e.key === "Tab") {
        // Keep focus inside the viewer while it is open.
        const focusable = dialogRef.current?.querySelectorAll<HTMLElement>("button, a[href]");
        if (!focusable || focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last?.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first?.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, index, items.length, onClose, onIndexChange]);

  if (!open || index === null) return null;
  const item = items[index];
  if (!item) return null;
  const count = items.length;

  return createPortal(
    <div className="gw-layer">
      <div className="gw-lb open" role="dialog" aria-modal="true" aria-label="Photo viewer" ref={dialogRef}>
        <button ref={closeRef} type="button" className="x" onClick={onClose} aria-label="Close photo viewer">
          <SiteIcon name="x" size={24} />
        </button>
        <button
          type="button"
          className="pv"
          onClick={() => onIndexChange((index - 1 + count) % count)}
          aria-label="Previous photo"
        >
          <SiteIcon name="left" size={26} />
        </button>
        <img src={item.src} alt={item.cap} />
        <div className="cap">
          {item.cap}
          {item.href ? (
            <>
              {" · "}
              <Link to={item.href} onClick={onClose} style={{ color: "var(--gold)" }}>
                View product
              </Link>
            </>
          ) : null}
          {` · ${index + 1} / ${count}`}
        </div>
        <button
          type="button"
          className="nx"
          onClick={() => onIndexChange((index + 1) % count)}
          aria-label="Next photo"
        >
          <SiteIcon name="right" size={26} />
        </button>
      </div>
    </div>,
    document.body,
  );
}
